/**
 * Pure liveness/moderation decisions for the voice-call watchdog.
 * Extracted from useVoiceCall so the regression around "admin drops out of a
 * 2-person call right after muting the other side" stays test-guarded.
 */
/**
 * Whether a peer's silence on the wire is EXPECTED and must never trigger the
 * no-media rebuild or the ghost-kick.
 *
 * A peer that told us (via an explicit media-state signal) that its mic is
 * muted is proof-of-life by itself: some browsers stop emitting RTP entirely
 * when the audio track is disabled, and even video RTP can stall briefly
 * around the mute. This exemption therefore applies in EVERY call type —
 * including a video call where the camera is nominally still on.
 *
 * (The old rule additionally required the camera to be off in a video call.
 * In a 2-person video call, the host muting the other side could then
 * escalate to a ghost-kick and a full teardown, dropping the ADMIN out of
 * the call. Missing video is handled by the separate, gentler video-rebuild
 * branch, which only renegotiates with backoff and never kicks.)
 */
export const isIntentionallySilent = (opts) => opts.remoteMicMuted;
/**
 * Whether an incoming ghost-kick addressed to us should be IGNORED (link
 * rebuilt instead of tearing the call down).
 *
 * A ghost-kick claims "no media from you EVER arrived". If media from the
 * SENDER reached us within the recent window, the link is demonstrably
 * alive in at least one direction and the claim is almost certainly a false
 * positive (e.g. an outdated client whose watchdog still treats a muted but
 * alive peer as dead). Tearing down the whole call — fatal in a 2-person
 * call — is the wrong response; renegotiating the link is safe either way.
 */
export const shouldIgnoreGhostKick = (opts) => !!opts.kickerMicMuted ||
    (opts.everHadMedia &&
        opts.lastMediaAt !== null &&
        opts.now - opts.lastMediaAt < opts.recentWindowMs);
/**
 * Decide what to do when we receive a ghost-kick that is validly addressed
 * to us and not a false positive (i.e. shouldIgnoreGhostKick returned false).
 *
 * - `drop-link-only`: other participants remain in the call; only the link to
 *   the kicking peer is torn down. The call continues with the rest.
 *   This is the key safety property for 3+ person calls: a ghost-kick from
 *   one peer must never evict a healthy third participant.
 *
 * - `full-teardown`: the kicking peer was our ONLY other participant; ending
 *   the call locally is appropriate.
 *
 * The `to` field on the ghost-kick signal is the first gate (only the
 * addressed target may react at all — see useVoiceCall.ts). This function
 * handles what happens once we have confirmed the signal is meant for us.
 */
export const ghostKickDisposition = (opts) => {
    const othersLeft = opts.participants.some((p) => p.deviceId !== opts.kickingPeer);
    return othersLeft ? 'drop-link-only' : 'full-teardown';
};
